import React from 'react';
import store2 from 'store2';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchDelete, fetchGet, fetchPost } from '../../store/fetch';
import { AssetTypes } from '../../utils/enums/AssetTypes';
import { PanelBody, PanelButtonLink, PanelHeading, PanelHorizontalSpacing, PanelSpacing, Tray } from '../elements';
import { AssetDropzone } from '../Surveys/Common/AssetDropzone';
import { AssetTemplateList } from '../Surveys/Common/AssetTemplateList';
import { IObjectTreeSelection } from '../Surveys/Common/ObjectTree';
import { IAsset } from '../Surveys/Interfaces/IAsset';
import { DocumentsList } from '../utils/DocumentsList';

// eslint-disable-next-line @typescript-eslint/naming-convention
export const ObjectEditDocuments = (props: { objectTreeSelection: IObjectTreeSelection; objectId: string }) => {
    const translate = useTranslate();
    const [isListView, setIsListView] = React.useState({ visible: false, updated: false });
    const [assets, setAssets] = React.useState<IAsset[]>([]);

    const loadAssets = async () => {
        const url = `/api/objects/${props.objectId}/assets`;
        const queryString = {
            types: `${AssetTypes.Document},${AssetTypes.Image},${AssetTypes.Image360},${AssetTypes.MeasurementXLS},${AssetTypes.MeasurementPDF},${AssetTypes.Text},${AssetTypes.BIM},${AssetTypes.ZipArchive},${AssetTypes.TarArchive},${AssetTypes.AutoCad},${AssetTypes.ImageTif},${AssetTypes.ImageAnnotation},${AssetTypes['3DModel']}`,
            elementId: props.objectTreeSelection.elementId,
            unitId: props.objectTreeSelection.unitId,
            manifestationId: props.objectTreeSelection.manifestationId,
            unassigned: !props.objectTreeSelection.elementId && !props.objectTreeSelection.unitId && !props.objectTreeSelection.manifestationId,
        };
        const result = await fetchGet(url, queryString, ['404']);
        setAssets(result.json);
    };

    React.useEffect(() => {
        void loadAssets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.objectTreeSelection]);

    React.useEffect(() => {
        if (store2.get('ObjectEditDocumentsListVisible') !== null) {
            setIsListView({ visible: store2.get('ObjectEditDocumentsListVisible') as boolean, updated: false });
        }
    }, []);

    React.useEffect(() => {
        if (isListView.updated) {
            store2.set('ObjectEditDocumentsListVisible', isListView.visible);
        }
    }, [isListView]);

    const onDropzoneComplete = async () => {
        await loadAssets();
    };

    async function removeDocument(assetId: string) {
        const url = `/api/objects/${props.objectId}/assets/${assetId}`;
        await fetchDelete(url);
        await loadAssets();
    }

    async function updateDocument(assetId: string, data: Record<string, unknown>) {
        const url = `/api/objects/${props.objectId}/assets/${assetId}`;
        await fetchPost(url, {}, data);
        await loadAssets();
    }

    return (
        <Tray>
            <PanelBody mb>
                <PanelHorizontalSpacing>
                    <AssetDropzone
                        acceptedFiles=".doc,.docx,.txt,.xls,.xlsx,.pdf,.zip,.tar,.dwg,.ifc,.png,.jpg,.jpeg,.tif,.tiff"
                        onComplete={onDropzoneComplete}
                        objectId={props.objectId}
                        elementId={props.objectTreeSelection.elementId}
                        manifestationId={props.objectTreeSelection.manifestationId}
                        unitId={props.objectTreeSelection.unitId}
                        autoProcessQueue={true}
                    />
                </PanelHorizontalSpacing>
            </PanelBody>

            <PanelHeading text={translate('survey.documents')} icon="fa fa-book">
                {isListView.visible ? (
                    <PanelButtonLink
                        onClick={() => setIsListView({ visible: false, updated: true })}
                        text={translate('equipment.gridView')}
                        icon="fa fa-th"
                        small={true}
                    />
                ) : (
                    <PanelButtonLink
                        onClick={() => setIsListView({ visible: true, updated: true })}
                        text={translate('equipment.listView')}
                        icon="fa fa-list"
                        small={true}
                    />
                )}
            </PanelHeading>

            <PanelBody noPadding={true}>
                {isListView.visible ? (
                    <DocumentsList
                        assets={assets}
                        objectId={props.objectId}
                        blueHeader={false}
                        onRemove={async (assetId: string) => removeDocument(assetId)}
                        onUpdate={updateDocument}
                        manageLabels={true}
                    />
                ) : (
                    <PanelSpacing>
                        <AssetTemplateList
                            assets={assets}
                            objectId={props.objectId}
                            canAddAnnotations={true}
                            canRemove={true}
                            onAssetUpdated={loadAssets}
                        />
                    </PanelSpacing>
                )}
            </PanelBody>
        </Tray>
    );
};
